import type {StoreApi} from 'zustand'
import type {DiffEntriesSlice} from '../stores/slices/diff-entries'
import type {ProgressiveDiffsSlice} from '../stores/slices/progressive-diffs'
import type {FileMetadataCacheResult} from '../page-data/loaders/use-file-metadata'

const PROGRESSIVE_DIFF_BATCH_SIZE = 4

export type ChangesLoadMode = 'progressive' | 'single'
type ProgressiveLoadMode = 'selected_file' | 'no_selected_file' | 'scroll'
type LoadState = Pick<ProgressiveDiffsSlice, 'entriesMap'> &
  Pick<DiffEntriesSlice, 'diffEntries' | 'diffEntriesRequestCount'>
type ObservableLoadState = LoadState & Pick<ProgressiveDiffsSlice, 'entriesOrder' | 'selectedPathDigest'>
type LoadMetricTags = {
  loadMode: ChangesLoadMode
  progressiveMode?: ProgressiveLoadMode
  navigationType: 'hard' | 'soft' | 'scroll'
  cacheSplit: 'enabled' | 'disabled'
  persistenceResult: FileMetadataCacheResult | 'not_applicable'
}
type PriorityLoad = {
  start: number
  pathDigests: Set<string>
}
export type LoadMetric = {
  name: 'PULL_REQUESTS_FILES_CHANGED_LOAD_DURATION'
  value: number
  tags: LoadMetricTags
}
export type DiffEntriesRequestCountMetric = {
  name: 'PULL_REQUESTS_FILES_CHANGED_DIFF_ENTRIES_REQUEST_COUNT'
  value: number
  tags: LoadMetricTags & {
    countType: 'planned' | 'actual'
  }
}

export function createChangesLoadTiming({
  cacheSplit,
  expectedMode,
  initialCacheResult,
  now,
  report,
  reportDiffEntriesRequestCount,
}: {
  cacheSplit: boolean
  expectedMode: ChangesLoadMode
  initialCacheResult?: FileMetadataCacheResult
  now: () => number
  report: (metric: LoadMetric) => void
  reportDiffEntriesRequestCount: (metric: DiffEntriesRequestCountMetric) => void
}) {
  let navigation: {start: number; type: 'hard' | 'soft'} | undefined
  let state: ObservableLoadState | undefined
  let duration: number | undefined
  let cacheResult = initialCacheResult
  let metadataSettled = !cacheSplit
  let active = false
  let cancelled = false
  let reported = false
  let plannedDiffEntriesRequests: number | undefined
  let initialDiffEntriesRequestCount: number | undefined
  let initialProgressiveMode: Exclude<ProgressiveLoadMode, 'scroll'> | undefined
  let initialSelectionCaptured = false
  let initialSelectedPathDigest: string | undefined
  let initialRequiredPathDigests: string[] | undefined
  let pendingPriorityLoads: PriorityLoad[] = []
  let pendingScrollDurations: number[] = []

  function tags(navigationType: LoadMetricTags['navigationType']): LoadMetricTags {
    const progressiveMode =
      expectedMode === 'progressive' ? (navigationType === 'scroll' ? 'scroll' : initialProgressiveMode) : undefined

    return {
      loadMode: expectedMode,
      ...(progressiveMode ? {progressiveMode} : {}),
      navigationType,
      cacheSplit: cacheSplit ? 'enabled' : 'disabled',
      persistenceResult: cacheResult ?? 'not_applicable',
    }
  }

  function reportIfReady() {
    if (!active || cancelled || reported || !navigation || duration === undefined) return
    if (cacheSplit && cacheResult === undefined) return

    reported = true
    const metricTags = tags(navigation.type)
    report({
      name: 'PULL_REQUESTS_FILES_CHANGED_LOAD_DURATION',
      value: duration,
      tags: metricTags,
    })
    reportDiffEntriesRequestCount({
      name: 'PULL_REQUESTS_FILES_CHANGED_DIFF_ENTRIES_REQUEST_COUNT',
      value: plannedDiffEntriesRequests ?? 0,
      tags: {...metricTags, countType: 'planned'},
    })
    reportDiffEntriesRequestCount({
      name: 'PULL_REQUESTS_FILES_CHANGED_DIFF_ENTRIES_REQUEST_COUNT',
      value: Math.max(0, (state?.diffEntriesRequestCount ?? 0) - (initialDiffEntriesRequestCount ?? 0)),
      tags: {...metricTags, countType: 'actual'},
    })
  }

  function reportPendingScrollLoads() {
    if (!active || cancelled || (cacheSplit && cacheResult === undefined)) return
    for (const value of pendingScrollDurations) {
      report({
        name: 'PULL_REQUESTS_FILES_CHANGED_LOAD_DURATION',
        value,
        tags: tags('scroll'),
      })
    }
    pendingScrollDurations = []
  }

  function checkPendingPriorityLoads() {
    if (!active || cancelled || !state) return

    const currentState = state
    const stillPending: PriorityLoad[] = []
    for (const load of pendingPriorityLoads) {
      if ([...load.pathDigests].every(pathDigest => isEntryReady(pathDigest, currentState))) {
        pendingScrollDurations.push(now() - load.start)
      } else {
        stillPending.push(load)
      }
    }
    pendingPriorityLoads = stillPending
    reportPendingScrollLoads()
  }

  function checkReady() {
    if (!active || cancelled || reported || !navigation || !state) return
    const currentState = state
    if (duration === undefined) {
      if (
        !metadataSettled ||
        !initialRequiredPathDigests?.every(pathDigest => isEntryReady(pathDigest, currentState))
      ) {
        return
      }
      duration = now() - navigation.start
    }
    reportIfReady()
  }

  return {
    start(value: NonNullable<typeof navigation>) {
      navigation ??= value
      active = true
      checkReady()
    },
    pause() {
      active = false
    },
    cancel() {
      cancelled = true
      pendingPriorityLoads = []
      pendingScrollDurations = []
    },
    recordCacheResult(value: FileMetadataCacheResult) {
      cacheResult = value
      reportIfReady()
      reportPendingScrollLoads()
    },
    recordMetadataSettled() {
      metadataSettled = true
      checkReady()
    },
    recordPriorityLoad(pathDigests: string[]) {
      if (expectedMode !== 'progressive' || !active || cancelled || !state) return
      if (!reported && state.selectedPathDigest === initialSelectedPathDigest) return
      const currentState = state
      const unloadedPathDigests = pathDigests.filter(
        pathDigest =>
          !isEntryReady(pathDigest, currentState) &&
          pendingPriorityLoads.every(load => !load.pathDigests.has(pathDigest)),
      )
      if (unloadedPathDigests.length === 0) return

      pendingPriorityLoads.push({start: now(), pathDigests: new Set(unloadedPathDigests)})
      checkPendingPriorityLoads()
    },
    observeLoadState(store: Pick<StoreApi<ObservableLoadState>, 'getState' | 'subscribe'>) {
      state = store.getState()
      if (!initialSelectionCaptured) {
        initialSelectedPathDigest = state.selectedPathDigest
        initialSelectionCaptured = true
      }
      initialProgressiveMode ??= state.selectedPathDigest ? 'selected_file' : 'no_selected_file'
      initialRequiredPathDigests ??= getRequiredLoadEntries(expectedMode, state)
      initialDiffEntriesRequestCount ??= state.diffEntriesRequestCount
      plannedDiffEntriesRequests ??= getPlannedDiffEntriesRequestCount(expectedMode, state)
      checkReady()
      return store.subscribe((next, previous) => {
        if (
          next.entriesMap === previous.entriesMap &&
          next.diffEntries === previous.diffEntries &&
          next.diffEntriesRequestCount === previous.diffEntriesRequestCount &&
          next.entriesOrder === previous.entriesOrder &&
          next.selectedPathDigest === previous.selectedPathDigest
        ) {
          return
        }
        state = next
        checkReady()
        checkPendingPriorityLoads()
      })
    },
  }
}

export type ChangesLoadTiming = ReturnType<typeof createChangesLoadTiming>

function getRequiredLoadEntries(mode: ChangesLoadMode, state: ObservableLoadState): string[] {
  if (mode === 'progressive') {
    return getProgressiveAutoLoadEntries(state)
  }

  const targetDigest = state.selectedPathDigest ?? state.entriesOrder[0]
  return targetDigest ? [targetDigest] : []
}

function isEntryReady(digest: string | undefined, state: LoadState) {
  if (!digest) return false
  const entry = state.entriesMap[digest]
  return entry?.isLoaded === true && state.diffEntries[entry.path]?.pathDigest === digest
}

function getPlannedDiffEntriesRequestCount(mode: ChangesLoadMode, state: ObservableLoadState) {
  if (state.entriesOrder.length === 0) return 0

  if (mode === 'progressive') {
    const unloadedEntries = getProgressiveAutoLoadEntries(state).filter(digest => !isEntryReady(digest, state)).length
    return Math.ceil(unloadedEntries / PROGRESSIVE_DIFF_BATCH_SIZE)
  }

  const targetDigest = state.selectedPathDigest ?? state.entriesOrder[0]
  return isEntryReady(targetDigest, state) ? 0 : 1
}

function getProgressiveAutoLoadEntries(
  state: Pick<ObservableLoadState, 'entriesOrder' | 'selectedPathDigest'>,
): string[] {
  const selectedIndex = state.selectedPathDigest ? state.entriesOrder.indexOf(state.selectedPathDigest) : -1
  return selectedIndex > 0 ? state.entriesOrder.slice(selectedIndex) : state.entriesOrder
}
